// ============================================================================
// raidBlueprint — pure Raid tuning helpers shared by the Firestore-backed
// workspace settings panel and the Raid forge preview. The
// server-authoritative payout / damage math lives in
// functions/src/experienceEngine.ts; these values are UI-preview multipliers
// plus defaults the Forge form uses when starting a new raid.
// ============================================================================

import { RAID_DAMAGE } from './gamificationRegistry'
import type { RaidView } from '../types/schema'

export type RaidRewardSplit = 'PROPORTIONAL' | 'EQUAL' | 'TOP_HEAVY'

export interface RaidBlueprint {
  hpTaskMultiplier: number
  hpChecklistMultiplier: number
  checklistDamage: number
  taskDamage: number
  focusDamage: number
  defaultEnrageDays: number
  defaultFunding: number
  rewardSplit: RaidRewardSplit
  autoPenaltyOnHighStakes: boolean
  defaultView: RaidView
  allowedViews: RaidView[]
  notifyOnStart: boolean
}

export const DEFAULT_RAID_BLUEPRINT: RaidBlueprint = {
  hpTaskMultiplier: 10,
  hpChecklistMultiplier: 5,
  checklistDamage: RAID_DAMAGE.CHECKLIST_COMPLETE,
  taskDamage: RAID_DAMAGE.TASK_COMPLETE,
  focusDamage: RAID_DAMAGE.FOCUS_BLOCK,
  defaultEnrageDays: 3,
  defaultFunding: 100,
  rewardSplit: 'PROPORTIONAL',
  autoPenaltyOnHighStakes: false,
  defaultView: 'WAR_ROOM',
  allowedViews: ['TIMELINE', 'CONSTELLATION', 'WAR_ROOM'],
  notifyOnStart: true,
}

const STORAGE_KEY_PREFIX = 'timelining:raid-blueprint:v1:'

export const raidBlueprintStorageKey = (workspaceId: string | null) =>
  `${STORAGE_KEY_PREFIX}${workspaceId ?? 'anonymous'}`

const VALID_REWARD_SPLITS: RaidRewardSplit[] = ['PROPORTIONAL', 'EQUAL', 'TOP_HEAVY']
const VALID_RAID_VIEWS: RaidView[] = ['TIMELINE', 'CONSTELLATION', 'WAR_ROOM']

const clampNumber = (value: unknown, fallback: number, min: number, max: number): number => {
  const numeric = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback
  return Math.max(min, Math.min(max, numeric))
}

export const normalizeRaidBlueprint = (raw: unknown): RaidBlueprint => {
  if (!raw || typeof raw !== 'object') return DEFAULT_RAID_BLUEPRINT
  const value = raw as Partial<RaidBlueprint>
  const defaultView = VALID_RAID_VIEWS.includes(value.defaultView as RaidView)
    ? value.defaultView as RaidView
    : DEFAULT_RAID_BLUEPRINT.defaultView
  const allowedViews = Array.isArray(value.allowedViews)
    ? VALID_RAID_VIEWS.filter((view) => value.allowedViews?.includes(view))
    : DEFAULT_RAID_BLUEPRINT.allowedViews
  const normalizedAllowedViews = allowedViews.includes(defaultView)
    ? allowedViews
    : [...allowedViews, defaultView]

  return {
    hpTaskMultiplier: clampNumber(value.hpTaskMultiplier, DEFAULT_RAID_BLUEPRINT.hpTaskMultiplier, 2, 40),
    hpChecklistMultiplier: clampNumber(value.hpChecklistMultiplier, DEFAULT_RAID_BLUEPRINT.hpChecklistMultiplier, 1, 20),
    checklistDamage: clampNumber(value.checklistDamage, DEFAULT_RAID_BLUEPRINT.checklistDamage, 1, 60),
    taskDamage: clampNumber(value.taskDamage, DEFAULT_RAID_BLUEPRINT.taskDamage, 20, 200),
    focusDamage: clampNumber(value.focusDamage, DEFAULT_RAID_BLUEPRINT.focusDamage, 40, 300),
    defaultEnrageDays: clampNumber(value.defaultEnrageDays, DEFAULT_RAID_BLUEPRINT.defaultEnrageDays, 1, 30),
    defaultFunding: clampNumber(value.defaultFunding, DEFAULT_RAID_BLUEPRINT.defaultFunding, 0, 100000),
    rewardSplit: VALID_REWARD_SPLITS.includes(value.rewardSplit as RaidRewardSplit)
      ? value.rewardSplit as RaidRewardSplit
      : DEFAULT_RAID_BLUEPRINT.rewardSplit,
    autoPenaltyOnHighStakes: typeof value.autoPenaltyOnHighStakes === 'boolean'
      ? value.autoPenaltyOnHighStakes
      : DEFAULT_RAID_BLUEPRINT.autoPenaltyOnHighStakes,
    defaultView,
    allowedViews: normalizedAllowedViews.length > 0 ? normalizedAllowedViews : [defaultView],
    notifyOnStart: typeof value.notifyOnStart === 'boolean'
      ? value.notifyOnStart
      : DEFAULT_RAID_BLUEPRINT.notifyOnStart,
  }
}

export const readLegacyRaidBlueprint = (workspaceId: string | null): RaidBlueprint | null => {
  if (typeof window === 'undefined' || !workspaceId) return null
  try {
    const raw = window.localStorage.getItem(raidBlueprintStorageKey(workspaceId))
    if (!raw) return null
    return normalizeRaidBlueprint(JSON.parse(raw))
  } catch {
    return null
  }
}

export const clearLegacyRaidBlueprint = (workspaceId: string | null): void => {
  if (typeof window === 'undefined' || !workspaceId) return
  try {
    window.localStorage.removeItem(raidBlueprintStorageKey(workspaceId))
  } catch { /* storage may be unavailable; safe to ignore */ }
}

/**
 * Preview HP using the admin-tuned multipliers (client-side only; the server
 * uses the canonical formula from gamificationRegistry when the raid actually
 * starts). Callers pass the pre-computed subtree counts.
 */
export const previewBossHp = (
  blueprint: RaidBlueprint,
  nestedTaskCount: number,
  weightMass: number,
  nestedChecklistCount: number,
): number =>
  Math.max(0, Math.round(
    Math.max(0, nestedTaskCount) * Math.max(0, weightMass) * blueprint.hpTaskMultiplier
    + Math.max(0, nestedChecklistCount) * blueprint.hpChecklistMultiplier,
  ))

export const REWARD_SPLIT_LABELS: Record<RaidRewardSplit, string> = {
  PROPORTIONAL: 'Proportional — damage share',
  EQUAL: 'Equal — every contributor',
  TOP_HEAVY: 'Top-heavy — MVP takes 60%',
}
